// components/TabBar.tsx — V29 S1: the bottom tab bar (replaces the LeftDrawer).
//
// The five current student sections (product-vocabulary) as a fixed bottom bar.
// Design contract (v29-build-plan §2.1/§2.6, amended by the founder device
// pass 2026-07-13 / DP3):
//   - ALWAYS visible on every student screen — exam, readers and detail pages
//     included ("the navigation bar should always be visible");
//   - the active tab stays lit on nested routes via sectionKeyForPath;
//   - taps are PURE navigation — same W3 semantics as the drawer rows: no
//     session/state reset, so a pending Simula finalize can never be erased
//     (F25). Tapping from a deep page returns to the section root; re-tapping
//     at the root is a no-op. Leaving a running exam is safe: the running
//     snapshot is persisted continuously and offers Riprendi on return;
//   - "Simula Esame" keeps its full name as the accessible label (F23); the
//     visible text is the full label too, sized to fit 5 tabs at 320px;
//   - Messaggi carries the unread badge (count from useUnreadMessages; the
//     store refreshes on section change + a slow visible-tab interval, since
//     the drawer-open reconciliation died with the drawer — F11).

import { useEffect, type JSX } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTokens } from '../state/theme';
import { useUnreadMessages } from '../state/unread-messages';
import { normalizeTabPath, sectionKeyForPath } from '../state/shell-chrome';
import { CURRENT_STUDENT_SECTIONS } from '../state/product-vocabulary';
import { MODE_ICONS } from './modeIcons';
import { TABBAR_H, BRAND_GRADIENT } from '../tokens';

/** Badge refresh cadence while the app tab is visible (F11). */
const UNREAD_REFRESH_MS = 60_000;

export function TabBar({ navigationLocked = false }: { navigationLocked?: boolean }): JSX.Element | null {
  const tokens = useTokens();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const unreadCount = useUnreadMessages((s) => s.count);
  const refreshUnread = useUnreadMessages((s) => s.refresh);

  const path = normalizeTabPath(pathname);
  const activeKey = sectionKeyForPath(pathname);

  // F11 — the drawer-open reconciliation is gone; refresh on route change…
  useEffect(() => {
    void refreshUnread();
  }, [path, refreshUnread]);
  // …and on a slow interval while the browser tab is visible.
  useEffect(() => {
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') void refreshUnread();
    }, UNREAD_REFRESH_MS);
    return () => window.clearInterval(id);
  }, [refreshUnread]);

  return (
    <nav
      data-testid="tabbar"
      aria-label="Sezioni"
      style={{
        position:      'absolute',
        left:          0,
        right:         0,
        bottom:        0,
        zIndex:        60,
        display:       'flex',
        alignItems:    'stretch',
        height:        `calc(${TABBAR_H}px + var(--sai-bottom))`,
        padding:       '6px 4px calc(4px + var(--sai-bottom))',
        background:    tokens.mode === 'dark' ? 'rgba(22,16,31,0.88)' : 'rgba(255,255,255,0.88)',
        backdropFilter: 'blur(18px)',
        WebkitBackdropFilter: 'blur(18px)',
        borderTop:     `1px solid ${tokens.line}`,
      }}
    >
      {CURRENT_STUDENT_SECTIONS.map((section) => {
        const active = activeKey === section.drawerModeKey;
        const atRoot = path === section.currentPath;
        const iconFn = MODE_ICONS[section.drawerModeKey];
        const color = active ? tokens.accentInk : tokens.textFaint;
        const showBadge = section.drawerModeKey === 'messaggi' && unreadCount > 0;
        return (
          <button
            key={section.id}
            type="button"
            data-testid={`tab-${section.drawerModeKey}`}
            aria-label={showBadge ? `${section.label} — ${unreadCount} non letti` : section.label}
            aria-current={active ? 'page' : undefined}
            disabled={navigationLocked}
            onClick={() => {
              // Pure navigation (W3 drawer parity): never resets session
              // state. From a deep page the tab returns to its section root.
              if (!navigationLocked && !atRoot) navigate(section.currentPath);
            }}
            style={{
              position:       'relative',
              flex:           1,
              minWidth:       0,
              display:        'flex',
              flexDirection:  'column',
              alignItems:     'center',
              justifyContent: 'center',
              gap:            3,
              border:         'none',
              background:     'transparent',
              borderRadius:   10,
              cursor:         navigationLocked ? 'not-allowed' : atRoot ? 'default' : 'pointer',
              color,
              padding:        0,
            }}
          >
            {/* Active indicator — a short gradient bar on the top edge, so the
                signal is never color-of-icon alone. */}
            {active && (
              <span
                aria-hidden="true"
                style={{
                  position:     'absolute',
                  top:          -6,
                  left:         '50%',
                  transform:    'translateX(-50%)',
                  width:        34,
                  height:       3,
                  borderRadius: '0 0 4px 4px',
                  background:   BRAND_GRADIENT[tokens.mode],
                }}
              />
            )}
            {iconFn(color, 22)}
            <span
              style={{
                fontFamily:    tokens.fonts.body,
                fontSize:      9.5,
                fontWeight:    700,
                letterSpacing: 0.1,
                whiteSpace:    'nowrap',
                maxWidth:      '100%',
                overflow:      'hidden',
                textOverflow:  'ellipsis',
              }}
            >
              {section.label}
            </span>
            {showBadge && (
              <span
                data-testid="tab-messaggi-badge"
                aria-hidden="true"
                style={{
                  position:       'absolute',
                  top:            2,
                  right:          'calc(50% - 22px)',
                  minWidth:       16,
                  height:         16,
                  padding:        '0 4px',
                  borderRadius:   8,
                  background:     tokens.accent,
                  color:          tokens.mode === 'dark' ? '#1a1510' : '#fff',
                  fontFamily:     tokens.fonts.body,
                  fontSize:       10,
                  fontWeight:     700,
                  lineHeight:     '16px',
                  textAlign:      'center',
                  border:         `1.5px solid ${tokens.bg}`,
                }}
              >
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
        );
      })}
    </nav>
  );
}
